import { useCallback, useEffect, useMemo, useState } from "react";

import PaginationMarkup from "./markup";
import { Props } from "./types";

function Pagination(props: Props) {
	const [itemsPerPage, setItemsPerPage] = useState(props.itemsPerPage);
	const [pages, setPages] = useState<number[]>([]);

	const { currentPage, totalItems, canSetItemsPerPage, pagesToDisplay = 10, pageSizes = [5, 10, 20] } = props;
	const totalPages = useMemo(
		() => (itemsPerPage === 0 ? 0 : Math.ceil(totalItems / itemsPerPage)),
		[totalItems, itemsPerPage],
	);

	const setPage = useCallback(
		function (page: number) {
			const maxPage = totalPages || 1;
			if (page < 1) page = 1;
			else if (page > maxPage) page = maxPage;
			if (props.pageChanged) props.pageChanged(page);
		},
		[props.pageChanged, totalPages],
	);

	useEffect(() => {
		if (canSetItemsPerPage) {
			props.setItemsPerPage(itemsPerPage);
		}
	}, [itemsPerPage]);

	useEffect(() => {
		if (currentPage !== 1) setPage(1);
	}, [props.itemsPerPage]);

	useEffect(() => {
		let startPage = 1,
			endPage = totalPages;
		const halfWay = Math.ceil(pagesToDisplay / 2);
		// if there aren't enough pages to display
		if (totalPages <= pagesToDisplay) {
			startPage = 1;
			endPage = totalPages;
		} else if (currentPage <= halfWay + 1) {
			startPage = 1;
			endPage = pagesToDisplay;
		} else if (totalPages - currentPage <= halfWay) {
			startPage = totalPages - pagesToDisplay;
			endPage = totalPages;
		} else {
			startPage = currentPage - halfWay;
			endPage = currentPage + halfWay - 1;
		}
		const newTotalPages = endPage + 1 - startPage;
		const newPages = newTotalPages > 0 ? [...new Array(newTotalPages)].map((v, i) => i + startPage) : [];
		setPages(newPages);
	}, [totalPages, currentPage]);

	return (
		<PaginationMarkup
			{...props}
			pageSizes={pageSizes}
			itemsPerPage={itemsPerPage}
			pages={pages}
			totalPages={totalPages}
			setPage={setPage}
			setItemsPerPage={setItemsPerPage}
		/>
	);
}

export default Pagination;
